import type { RouteRecordRaw } from 'vue-router';

import { wrapRuntimeImport } from '@/shared/helpers/async.helpers';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

// The MSP-partner surface. Today only ZynoTalk quoting; Suite/Display quoting will slot in beside it
// under the same `/msp` tree. Every route carries `msp: true` so both the route guard and the sidebar
// (which reads hasRouteAccess per path) gate on the msp.login capability — see computeAuthorized.
// The flag is repeated on every record on purpose: routesWithAuthorization reads each record's OWN
// meta (vue-router only merges parent meta when *resolving* a location, not on the stored records),
// so a child that omitted it would fail open.
export const MspRoutes: RouteRecordRaw[] = [
    {
        path: '',
        // Land on the first entry the partner can actually see, so clicking "MSP" in the sidebar
        // doesn't skip past Tenants to the item below it. Kept a function rather than pointing
        // statically at Tenants: routesWithAuthorization resolves a redirect to its destination's
        // authorization, so a static one would make /msp itself unauthorized for a quote-only
        // partner — taking the whole MSP entry out of their sidebar.
        redirect: () => (hasPermission('msp', 'inventoryRead') ? '/msp/tenants' : '/msp/talk-quotes')
    },
    {
        // The cross-tenant overview. `feature` narrows the gate to msp.inventoryRead on top of the
        // msp.login every route here requires — a partner may quote without being able to read the
        // whole client book.
        path: 'tenants',
        component: wrapRuntimeImport(() => import('./screens/tenants.vue')),
        meta: { msp: true, feature: 'inventoryRead', subAppName: 'Tenants' }
    },
    {
        // Component-less nesting record: the builder screens are CHILDREN of the list route rather
        // than siblings so the sidebar's "Talk Quotes" entry stays lit on /msp/talk-quotes/new and
        // /msp/talk-quotes/:id. RouterLink's isActive is route-record based, not path-prefix based —
        // sibling records never match, which is why the entry went dark on the builder. Records with
        // no component are skipped by RouterView, so these still render in msp.vue's outlet.
        path: 'talk-quotes',
        meta: { msp: true },
        children: [
            {
                path: '',
                component: wrapRuntimeImport(() => import('./screens/talk-quotes.vue')),
                meta: { msp: true, subAppName: 'Talk Quotes' }
            },
            {
                // A fresh draft. Kept distinct from the `:id` route so "New quote" never collides with
                // an order id, and so the builder can tell "start empty" from "resume this order".
                path: 'new',
                component: wrapRuntimeImport(() => import('./screens/talk-quote-builder.vue')),
                meta: { msp: true, subAppName: 'New Quote' }
            },
            {
                path: ':id',
                component: wrapRuntimeImport(() => import('./screens/talk-quote-builder.vue')),
                meta: { msp: true, subAppName: 'Quote' }
            }
        ]
    }
];
